import {ModalBody, ModalFooter, ModalOverlay, ModalXCloseButton} from '@components/ui/modal-atoms/ModalAtoms.tsx'
import {
    StyledActiveCard,
    StyledDoctorTherapistControls,
    StyledModal,
    StyledModalHeader,
    StyledModalTitle
} from '@/features/treatment-requests/components/doctor-therapist-selection-modal/style.ts'
import {useTranslation} from '@/translations/i18n.tsx'
import {ChangeEvent, Fragment, ReactNode} from 'react'
import {Button} from '@components/ui/button/Button.tsx'
import {InputText} from '@components/commons/input-text/InputText.tsx'
import {AlertCircleIcon, SearchSmIcon} from '@components/ui/icon/Icon.tsx'
import {useTheme} from 'styled-components'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {Badge} from '@components/ui/badge/Badge.tsx'
import {TreatmentRequestSmall, TreatmentRequestTherapistDoctor} from '@/features/treatment-requests/types.ts'
import {makeDiseaseTreePath, treatmentRequestSectorToLabel} from '@/features/treatment-requests/utils.ts'
import {patientServiceTypeMapToLabel} from '@utilities/helpers.ts'
import {Spinner} from '@components/ui/spinner/Spinner.tsx'
import {ErrorBox} from '@components/commons/error-box/ErrorBox.tsx'
import {EmptyState} from '@/components/commons/empty-state/EmptyState'
import {DoctorTherapistDatesRange} from '../doctor-therapist-dates-range/DoctorTherapistDatesRange'
import {Divider} from '@/components/ui/divider/Divider'

interface DoctorTherapistSelectionModalProps<T extends TreatmentRequestTherapistDoctor> {
    title: string
    treatmentRequest: TreatmentRequestSmall
    selectionList: T[]
    renderSelectionListItem: (dataItem: T, index: number) => ReactNode
    onClose: () => void
    onSearch: (event: ChangeEvent<HTMLInputElement>) => void
    isLoading: boolean
    isError: boolean
    onConfirm: () => void
    isConfirmLoading: boolean
    dateRange: [string, string]
    setDateRange: (dateRange: [string, string]) => void
}
export const DoctorTherapistSelectionModal = <T extends TreatmentRequestTherapistDoctor>({
    title,
    onClose,
    onSearch,
    selectionList,
    renderSelectionListItem,
    treatmentRequest,
    isLoading,
    isError,
    onConfirm,
    isConfirmLoading,
    dateRange,
    setDateRange
}: DoctorTherapistSelectionModalProps<T>) => {
    const {t} = useTranslation()
    const theme = useTheme()
    const diseaseTreePath = makeDiseaseTreePath(treatmentRequest)

    return (
        <>
            <ModalOverlay
                onClick={event => {
                    event.stopPropagation()
                    onClose()
                }}
            />
            <StyledModal width="450px" onClick={event => event.stopPropagation()}>
                <StyledModalHeader>
                    <Flexbox justify="space-between" align="center">
                        <StyledModalTitle>{title}</StyledModalTitle>
                        <ModalXCloseButton onClick={onClose} />
                    </Flexbox>
                    <StyledActiveCard>
                        <Flexbox align="center" justify="space-between">
                            <h3>{t('treatment_requests:for_patient', {fullName: treatmentRequest.patientFullName})}</h3>
                            <Badge shape="rectangle">
                                {t(patientServiceTypeMapToLabel[treatmentRequest.serviceType])} -{' '}
                                {t(treatmentRequestSectorToLabel[treatmentRequest.sector])}
                            </Badge>
                        </Flexbox>
                        <p>
                            {diseaseTreePath[0]}
                            {diseaseTreePath.length >= 2 && ` - ${diseaseTreePath.at(-1)}`}
                        </p>
                    </StyledActiveCard>
                </StyledModalHeader>
                <ModalBody style={{paddingTop: 0}}>
                    <StyledDoctorTherapistControls direction="column" gap={4}>
                        <InputText
                            type="text"
                            typeIcon={<SearchSmIcon size={16} fill={theme.palette.neutral['500']} />}
                            placeholder={t('commons:search_placeholder')}
                            onChange={onSearch}
                        />

                        {treatmentRequest.sector == 'outpatient' && (
                            <>
                                <DoctorTherapistDatesRange
                                    isDisabled={isLoading}
                                    dateRange={dateRange}
                                    setDateRange={setDateRange}
                                />
                                <Divider direction="horizontal" />
                            </>
                        )}
                    </StyledDoctorTherapistControls>

                    {isLoading ? (
                        <Spinner size={20} style={{alignSelf: 'center'}} />
                    ) : isError ? (
                        <ErrorBox icon={<AlertCircleIcon />} title={t('errors:default')} />
                    ) : selectionList.length == 0 ? (
                        <EmptyState title={t('commons:no_results')} />
                    ) : (
                        <Flexbox direction="column" gap={4}>
                            {selectionList.map((selectionItem, index) => (
                                <Fragment key={`${selectionItem.id}-${index}`}>
                                    {renderSelectionListItem(selectionItem, index)}
                                </Fragment>
                            ))}
                        </Flexbox>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button onClick={onClose} variant="secondary" disabled={isConfirmLoading}>
                        {t('commons:close')}
                    </Button>
                    <Button type="submit" variant="primaryNeutral" onClick={onConfirm} disabled={isConfirmLoading}>
                        {t('commons:confirm')}
                        {isConfirmLoading && <Spinner size={16} />}
                    </Button>
                </ModalFooter>
            </StyledModal>
        </>
    )
}
